import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, IconButton,
  Avatar, Menu, MenuItem, Divider, Chip
} from '@mui/material';
import BusinessIcon     from '@mui/icons-material/Business';
import DashboardIcon    from '@mui/icons-material/Dashboard';
import RouterIcon       from '@mui/icons-material/Router';
import ExploreIcon      from '@mui/icons-material/Explore';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import TuneIcon         from '@mui/icons-material/Tune';
import PeopleIcon       from '@mui/icons-material/People';
import MapIcon          from '@mui/icons-material/Map';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import ArticleIcon from '@mui/icons-material/Article';
import FlashOnIcon  from '@mui/icons-material/FlashOn';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import LogoutIcon       from '@mui/icons-material/Logout';
import MenuIcon         from '@mui/icons-material/Menu';
import { useAuth }      from '../contexts/AuthContext';

const DRAWER_WIDTH = 240;

const navItems = [
  { path: '/clients',     label: 'Clients',          icon: <BusinessIcon /> },
  { path: '/sites',       label: 'Sites',            icon: <LocationCityIcon /> },
  { path: '/dashboard',   label: 'Tableau de bord',  icon: <DashboardIcon /> },
  { path: '/supervision', label: 'Capteurs',          icon: <RouterIcon /> },
  { path: '/walkthrough', label: 'Walkthrough',       icon: <ExploreIcon /> },
  { path: '/calibration', label: 'Calibration',       icon: <TuneIcon /> },
  { path: '/campagne',    label: 'Campagne',          icon: <MyLocationIcon /> },
  { path: '/enrollment',  label: 'Employes',          icon: <PeopleIcon /> },
  { path: '/zones',       label: 'Zones',             icon: <MapIcon /> },
  { path: '/provisioning', label: 'Provisioning',      icon: <SettingsInputAntennaIcon /> },
  { path: '/logs',         label: 'Logs capteurs',      icon: <ArticleIcon /> },
  { path: '/flash',        label: 'Flash capteur',      icon: <FlashOnIcon /> },
];

export default function Layout() {
  const { user, logout }    = useAuth();
  const navigate            = useNavigate();
  const location            = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl]     = useState(null);

  const drawer = (
    <Box sx={{ height: '100%', bgcolor: '#1F4E79', color: 'white',
      display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <img src="/logo-redia.jpg" alt="REDIA"
          style={{ width: 32, height: 32, objectFit: 'contain' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
          REDIA Tools
        </Typography>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)' }} />
      <List sx={{ mt: 1, flexGrow: 1 }}>
        {navItems.map(item => {
          const active = location.pathname === item.path ||
            (item.path === '/clients' && location.pathname === '/');
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                sx={{
                  mx: 1, borderRadius: 2,
                  bgcolor: active ? 'rgba(255,255,255,0.15)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}>
                <ListItemIcon sx={{ color: active ? '#90CAF9' : 'rgba(255,255,255,0.7)',
                  minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.label}
                  sx={{ '& .MuiTypography-root': {
                    color: active ? 'white' : 'rgba(255,255,255,0.8)',
                    fontWeight: active ? 600 : 400, fontSize: '0.9rem'
                  }}} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* User info bas du drawer */}
      <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
        <Chip
          label={user?.role === 'ADMIN_REDIA' ? 'Admin REDIA' : 'Client'}
          size="small"
          sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white',
            fontSize: '0.75rem', mb: 1 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,
          cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
          onClick={e => setAnchorEl(e.currentTarget)}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: '#2E75B6', fontSize: '0.8rem' }}>
            {user?.full_name?.[0] || 'U'}
          </Avatar>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography sx={{ color: 'white', fontSize: '0.82rem',
              fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden',
              textOverflow: 'ellipsis' }}>
              {user?.full_name}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.email}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar mobile */}
      <AppBar position="fixed"
        sx={{ display: { md: 'none' }, bgcolor: '#1F4E79',
          zIndex: t => t.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton color="inherit" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>
          <img src="/logo-redia.jpg" alt="REDIA"
            style={{ width: 24, height: 24, objectFit: 'contain', marginRight: 8 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            REDIA Tools
          </Typography>
          <IconButton color="inherit" onClick={e => setAnchorEl(e.currentTarget)}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#2E75B6', fontSize: '0.8rem' }}>
              {user?.full_name?.[0] || 'U'}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Drawer mobile */}
      <Drawer variant="temporary" open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{ display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box',
            border: 'none' }}}>
        {drawer}
      </Drawer>

      {/* Drawer desktop */}
      <Drawer variant="permanent"
        sx={{ display: { xs: 'none', md: 'block' }, width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box',
            border: 'none' }}}>
        {drawer}
      </Drawer>

      {/* Menu utilisateur */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}>
        <MenuItem onClick={() => { logout(); navigate('/login'); setAnchorEl(null); }}>
          <LogoutIcon sx={{ mr: 1, fontSize: 18 }} /> Deconnexion
        </MenuItem>
      </Menu>

      {/* Contenu principal */}
      <Box component="main" sx={{
        flexGrow: 1, p: 3, bgcolor: '#F5F7FA', minHeight: '100vh',
        mt: { xs: 8, md: 0 },
        width: { md: `calc(100% - ${DRAWER_WIDTH}px)` }
      }}>
        <Outlet />
      </Box>
    </Box>
  );
}
